import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { base64Encode, base64Decode, base62ToDecimal } from "../utils/crypto";
import {
  userLogin,
  userLogout,
  getWalletBalance,
  loadCachedBasicData,
  clearCachedBasicData,
} from "../services/api";

const AuthContext = createContext();

// Token 过期时间：30分钟（毫秒）
const TOKEN_EXPIRE_TIME = 30 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [currentMode, setCurrentMode] = useState(
    localStorage.getItem("winburst-mode") || "sc",
  );

  const [isLoading, setIsLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);

  // 同步 currentMode
  const syncCurrentMode = useCallback(() => {
    setCurrentMode(localStorage.getItem("winburst-mode") || "sc");
  }, []);

  // Load user from localStorage on mount
  useEffect(() => {
    syncCurrentMode();

    const loadUser = () => {
      try {
        // Load cached basic data first
        loadCachedBasicData();

        const encryptedUser = localStorage.getItem("winburst_user");
        if (encryptedUser) {
          const decryptedUser = JSON.parse(base64Decode(encryptedUser));

          // 检查 token 是否过期
          if (decryptedUser.token_created_at) {
            const tokenAge = Date.now() - decryptedUser.token_created_at;
            if (tokenAge >= TOKEN_EXPIRE_TIME) {
              console.log("[AuthContext] Token 已过期，清除旧数据");
              localStorage.removeItem("winburst_user");
              clearCachedBasicData();
              setShowLoginModal(true);
              setIsLoading(false);
              return;
            }
          }

          setUser(decryptedUser);
        } else {
          setShowLoginModal(true);
        }
      } catch (error) {
        console.error("Failed to load user:", error);
        setShowLoginModal(true);
      }
      setIsLoading(false);
    };

    loadUser();
  }, [syncCurrentMode]);

  // Save user to localStorage (encrypted)
  const saveUser = useCallback((userData) => {
    // 添加 token 创建时间
    const userDataWithTimestamp = {
      ...userData,
      token_created_at: Date.now(),
    };
    const encryptedUser = base64Encode(JSON.stringify(userDataWithTimestamp));
    localStorage.setItem("winburst_user", encryptedUser);
  }, []);

  // Clear user from localStorage
  const clearUser = useCallback(() => {
    localStorage.removeItem("winburst_user");
    clearCachedBasicData();
    setUser(null);
    setWallet(null);
  }, []);

  // Check if token is expired
  const isTokenExpired = useCallback(() => {
    if (!user || !user.token_created_at) {
      return true;
    }
    return Date.now() - user.token_created_at >= TOKEN_EXPIRE_TIME;
  }, [user]);

  // Check and clear expired token
  const checkAndClearExpiredToken = useCallback(() => {
    if (isTokenExpired()) {
      console.log("[AuthContext] Token 已过期，清除并触发重新登录");
      clearUser();
      setShowLoginModal(true);
      return true;
    }
    return false;
  }, [isTokenExpired, clearUser]);

  // Login success callback - can be set by components
  const onLoginSuccessCallbackRef = useRef(null);
  const setOnLoginSuccess = useCallback((callback) => {
    console.log("[AuthContext] setOnLoginSuccess called");
    onLoginSuccessCallbackRef.current = callback;
  }, []);

  // Login function
  const login = useCallback(
    async (email, password) => {
      try {
        const response = await userLogin(email, password);

        if (response.user_info && response.user_token) {
          // Convert user_id from base62 to decimal for s_user_id
          const userInfo = {
            ...response.user_info,
            user_token: response.user_token,
            s_user_id: base62ToDecimal(response.user_info.user_id),
          };

          // Save encrypted user data
          saveUser(userInfo);
          setUser(userInfo);

          // Fetch wallet after login
          await fetchWallet(userInfo.user_id);

          setShowLoginModal(false);

          // Trigger login success callback
          console.log(
            "[AuthContext] Login success, checking callback:",
            !!onLoginSuccessCallbackRef.current,
          );
          if (onLoginSuccessCallbackRef.current) {
            console.log("[AuthContext] Calling onLoginSuccess callback");
            onLoginSuccessCallbackRef.current(userInfo);
          }

          return { success: true, user: userInfo };
        } else {
          throw new Error("Invalid login response");
        }
      } catch (error) {
        console.error("Login error:", error);
        return { success: false, error: error.message };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [saveUser],
  );

  // Logout function
  const logout = useCallback(async () => {
    try {
      if (user && user.user_id) {
        await userLogout(user.user_id);
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearUser();
      setShowLoginModal(true);
    }
  }, [user, clearUser]);

  // Fetch wallet balance
  const fetchWallet = useCallback(async (userId) => {
    try {
      const walletData = await getWalletBalance(userId);
      setWallet(walletData);
      return walletData;
    } catch (error) {
      console.error("Fetch wallet error:", error);
      throw error;
    }
  }, []);

  // Refresh wallet (called from components)
  const refreshWallet = useCallback(async () => {
    if (user && user.user_id) {
      return await fetchWallet(user.user_id);
    }
  }, [user, fetchWallet]);

  // Calculate SC (ts_money + rd_money + gm_money)
  const getSCBalance = useCallback(() => {
    if (!wallet) return 0;
    return (
      (wallet.ts_money || 0) + (wallet.rd_money || 0) + (wallet.gm_money || 0)
    );
  }, [wallet]);

  // Calculate GC (coin)
  const getGCBalance = useCallback(() => {
    if (!wallet) return 0;
    return wallet.coin || 0;
  }, [wallet]);

  // Mode switching
  const switchMode = useCallback((mode) => {
    setCurrentMode(mode);
    localStorage.setItem("winburst-mode", mode);
  }, []);

  // Get user info for display
  const getUserInfo = useCallback(() => {
    if (!user) return null;
    return {
      userId: user.user_id,
      nickname: user.nickname || "Unknown",
      vipLevel: user.vip_level || 0,
      sUserId: user.s_user_id || "N/A",
    };
  }, [user]);

  const value = {
    user,
    wallet,
    currentMode,
    isLoading,
    showLoginModal,
    showUserInfoModal,
    setShowLoginModal,
    setShowUserInfoModal,
    login,
    logout,
    refreshWallet,
    getSCBalance,
    getGCBalance,
    getUserInfo,
    switchMode,
    syncCurrentMode,
    setOnLoginSuccess,
    isTokenExpired,
    checkAndClearExpiredToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
